import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, Autocomplete } from '@react-google-maps/api';
import { AdvancedMarker } from './AdvancedMarker';
import { useGoogleMaps } from './GoogleMapsProvider';

const mapContainerStyle = {
  width: '100%',
  height: '300px',
};

const defaultCenter = {
  lat: 9.019, // Default latitude (Addis Ababa)
  lng: 38.75, // Default longitude (Addis Ababa)
};

interface Location {
  lat: number;
  lng: number;
  address: string;
  displayName?: string; // New field for simplified display
  fullAddress?: string; // Keep the full address for reference
}

interface GoogleMapLocationPickerProps {
  onLocationSelect: (location: Location) => void;
  initialLocation?: Location | null;
  allowManualEdit?: boolean; // New prop to enable manual editing
}

const GoogleMapLocationPicker: React.FC<GoogleMapLocationPickerProps> = ({
  onLocationSelect,
  initialLocation,
  allowManualEdit = true, // Default to true
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    initialLocation || null
  );
  const [searchValue, setSearchValue] = useState<string>(initialLocation?.address || '');
  const [displayName, setDisplayName] = useState<string>(initialLocation?.displayName || '');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const center = selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : defaultCenter;

  useEffect(() => {
    if (!isLoaded) return;
    if (initialLocation && initialLocation.lat && initialLocation.lng) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat: initialLocation.lat, lng: initialLocation.lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const fullAddress = results[0].formatted_address;
          const simplifiedDisplay = extractSimplifiedLocation(results[0]);
          setSelectedLocation({ 
            ...initialLocation, 
            address: fullAddress,
            displayName: simplifiedDisplay || initialLocation.displayName || fullAddress,
          });
          setSearchValue(fullAddress);
          setDisplayName(simplifiedDisplay || initialLocation.displayName || fullAddress);
        }
      });
    }
  }, [isLoaded, initialLocation]);

  // New function to extract business name and city
  const extractSimplifiedLocation = (result: google.maps.GeocoderResult) => {
    // Try to get business name or prominent point of interest
    let businessName = '';
    
    // If place name exists, use it as business name
    if (result.name) {
      businessName = result.name;
    } 
    // Otherwise try to get it from the first part of the address
    else {
      businessName = result.formatted_address.split(',')[0];
    }
    
    // Try to find the city component
    let city = '';
    if (result.address_components) {
      for (const component of result.address_components) {
        if (component.types.includes('locality') || component.types.includes('administrative_area_level_2')) {
          city = component.long_name;
          break;
        }
      }
      
      // If city not found, try to get administrative area
      if (!city) {
        for (const component of result.address_components) {
          if (component.types.includes('administrative_area_level_1')) {
            city = component.long_name;
            break;
          }
        }
      }
    }
    
    // Format the display name
    if (businessName && city) {
      return `${businessName}, ${city}`;
    } else if (businessName) {
      return businessName;
    }
    
    return '';
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (!place.geometry) {
        console.log("No details available for input: '" + place.name + "'");
        return;
      }
      
      // Generate both full address and simplified display
      const fullAddress = place.formatted_address || '';
      const simplifiedDisplay = place.name 
        ? `${place.name}${place.formatted_address ? `, ${place.formatted_address.split(',')[place.formatted_address.split(',').length-2]?.trim() || ''}` : ''}`
        : extractSimplifiedLocation(place as any);
      
      const location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        address: fullAddress,
        displayName: simplifiedDisplay || fullAddress,
        fullAddress: fullAddress
      };
      
      setSelectedLocation(location);
      setSearchValue(fullAddress);
      setDisplayName(simplifiedDisplay || fullAddress);
      onLocationSelect(location);
      
      if (mapRef.current) {
        mapRef.current.panTo(location);
      }
    }
  };

  const onMapClick = async (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: event.latLng }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const fullAddress = results[0].formatted_address;
          const simplifiedDisplay = extractSimplifiedLocation(results[0]);
          
          const location = {
            lat: event.latLng!.lat(),
            lng: event.latLng!.lng(),
            address: fullAddress,
            displayName: simplifiedDisplay || fullAddress,
            fullAddress: fullAddress
          };
          
          setSelectedLocation(location);
          setSearchValue(fullAddress);
          setDisplayName(simplifiedDisplay || fullAddress);
          onLocationSelect(location);
        }
      });
    }
  };

  // Handle display name changes
  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDisplayName = e.target.value;
    setDisplayName(newDisplayName);
    
    if (selectedLocation) {
      const updatedLocation = {
        ...selectedLocation,
        displayName: newDisplayName
      };
      setSelectedLocation(updatedLocation);
      onLocationSelect(updatedLocation);
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google && map) {
      if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
        // Create an advanced marker if available
        new window.google.maps.marker.AdvancedMarkerElement({
          position: center,
          map,
          title: 'Selected Location',
        });
      } else {
        // Fallback to the standard marker
        new window.google.maps.Marker({
          position: center,
          map,
          title: 'Selected Location',
        });
      }
    }
  }, [center]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading Maps...</div>;

  return (
    <div>
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
          autocomplete.addListener('place_changed', onPlaceChanged);
        }}
      >
        <input
          type="text"
          placeholder="Search for a location"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          ref={inputRef}
          style={{
            boxSizing: 'border-box',
            border: '1px solid transparent',
            width: '100%',
            height: '40px',
            padding: '0 12px',
            borderRadius: '3px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
            fontSize: '14px',
            outline: 'none',
            textOverflow: 'ellipses',
            marginBottom: '10px',
            position: 'relative',
            zIndex: 9999,
          }}
        />
      </Autocomplete>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={selectedLocation ? 15 : 12}
        center={center}
        onClick={onMapClick}
        onLoad={onMapLoad}
        options={{ mapId: 'WARKA_MAP' }}
      >
        {selectedLocation && <AdvancedMarker position={selectedLocation} />}
      </GoogleMap>
      
      {/* Manual edit field for display name */}
      {allowManualEdit && selectedLocation && (
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name (edit for simplicity)
          </label>
          <input
            type="text"
            value={displayName}
            onChange={handleDisplayNameChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Business name, City"
          />
          <p className="text-xs text-gray-500 mt-1">
            Edit for a simpler display like "Business Name, City"
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleMapLocationPicker;